import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";

// headerFields like EntityDialog; supports line items with item + quantity + batch.
export default function OperationDialog({ open, onOpenChange, title, headerFields, endpoint, onSaved }) {
  const { formatApiError } = useAuth();
  const items = useOptions("/items");
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setForm({}); setLines([]); } }, [open]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addLine = () => setLines((l) => [...l, { item_id: "", quantity: 1, batch: "" }]);
  const setLine = (i, k, v) => setLines((l) => l.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const submit = async () => {
    for (const f of headerFields) if (f.required && !form[f.name]) return toast.error(`${f.label} required`);
    if (lines.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = { ...form };
      headerFields.forEach((f) => { if (f.type === "number" && payload[f.name]) payload[f.name] = Number(payload[f.name]); });
      payload.items = lines.map((ln) => {
        const it = items.find((o) => o.value === ln.item_id)?.raw;
        return { item_id: ln.item_id, item_name: it?.name, sku: it?.sku,
                 quantity: Number(ln.quantity), unit_cost: it?.cost_price, batch: ln.batch || null };
      });
      await api.post(endpoint, payload);
      toast.success("Created successfully");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="operation-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {headerFields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
              {f.type === "select" ? (
                <Select value={String(form[f.name] ?? "")} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`opf-${f.name}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                  <SelectContent>{f.options?.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid={`opf-${f.name}`} type={f.type === "number" ? "number" : "text"}
                  value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Line Items</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="h-4 w-4 mr-1" />Add Line</Button>
          </div>
          <div className="space-y-2">
            {lines.length === 0 && <p className="text-sm text-slate-400 py-3 text-center border border-dashed rounded-md">No line items yet.</p>}
            {lines.map((ln, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`line-${i}`}>
                <Select value={ln.item_id} onValueChange={(v) => setLine(i, "item_id", v)}>
                  <SelectTrigger className="flex-1" data-testid={`line-item-${i}`}><SelectValue placeholder="Select item" /></SelectTrigger>
                  <SelectContent>{items.map((o) => <SelectItem key={o.value} value={o.value}>{o.raw.sku} · {o.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" className="w-20" placeholder="Qty" value={ln.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`line-qty-${i}`} />
                <Input className="w-24" placeholder="Batch" value={ln.batch}
                  onChange={(e) => setLine(i, "batch", e.target.value)} />
                <button onClick={() => removeLine(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-md"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="op-save" className="bg-blue-600 hover:bg-blue-700">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
